/**
 * Действия над аккаунтом в строке таблицы (KAN-609): включить / выключить,
 * переключатель "Писать первым". Только для аккаунтов нового держателя.
 * KAN-741: удаления нет, аккаунт выводится из работы кнопкой "Списать".
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ConfirmDialog } from '@/components/telegram/ConfirmDialog';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  DISABLE_CONFIRM,
  FORCE_DISABLE_CONFIRM,
  isFreshRefusal,
  isPersonalTelegramAccount,
  needsDisableConfirm,
  outreachErrorMessage,
  toggleActionView,
  toggleErrorMessage,
} from '@/lib/telegramAccountActions';
import { telegramAccountsApi } from '@/services/api/telegramAccounts';
import type { TelegramAccount } from '@/types/telegramAccount';

const QUERY_KEY = ['telegram-accounts'];
const BTN = 'h-7 px-2 text-xs';

const keyOf = (a: TelegramAccount): string => `${a.operator_id}-${a.account_id}`;

type Dialog = 'disable' | 'force' | null;

/**
 * Кнопка "Включить/Выключить" под статусом соединения.
 * KAN-871: выключенный личный аккаунт менеджера из кабинета не включается
 * (backend 409), кнопки нет; выключить включённый можно.
 */
export function AccountActions({
  account,
}: {
  account: TelegramAccount;
}): JSX.Element | null {
  const queryClient = useQueryClient();
  const key = keyOf(account);
  const [dialog, setDialog] = useState<Dialog>(null);
  const [fresh, setFresh] = useState<string | null>(null);
  const view = toggleActionView(account.enabled);

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
  };

  const toggle = useMutation({
    mutationFn: (v: { enabled: boolean; force?: boolean }) =>
      telegramAccountsApi.updateAccount(
        account.account_id,
        v.force ? { enabled: v.enabled, force: true } : { enabled: v.enabled },
        account.operator_id
      ),
    onSuccess: () => {
      setFresh(null);
      refresh();
    },
    onError: (err, v) => {
      const text = toggleErrorMessage(err);
      toast.error(text);
      // force не предлагается автоматически: только явная ссылка под кнопками
      if (!v.force && isFreshRefusal(err)) setFresh(text);
    },
  });

  const busy = toggle.isPending;

  const onToggle = (): void => {
    setFresh(null);
    if (!view.next && needsDisableConfirm(account)) setDialog('disable');
    else toggle.mutate({ enabled: view.next });
  };

  if (isPersonalTelegramAccount(account) && !account.enabled) return null;

  return (
    <div className="mt-1.5 flex flex-col items-start gap-1">
      <div className="flex gap-1.5">
        <Button
          variant="outline"
          size="sm"
          className={BTN}
          disabled={busy}
          data-testid={`toggle-${key}`}
          onClick={onToggle}
        >
          {toggle.isPending ? '...' : view.label}
        </Button>
      </div>
      {fresh && (
        <div data-testid={`fresh-refusal-${key}`} className="text-xs text-red-700">
          <span>{fresh}</span>{' '}
          <button
            type="button"
            className="underline"
            disabled={busy}
            data-testid={`force-disable-${key}`}
            onClick={() => setDialog('force')}
          >
            Выключить всё равно
          </button>
        </div>
      )}
      <ConfirmDialog
        testId={`disable-dialog-${key}`}
        open={dialog === 'disable'}
        onOpenChange={(o) => !o && setDialog(null)}
        view={DISABLE_CONFIRM}
        onConfirm={() => toggle.mutate({ enabled: false })}
      />
      <ConfirmDialog
        testId={`force-dialog-${key}`}
        open={dialog === 'force'}
        onOpenChange={(o) => !o && setDialog(null)}
        view={FORCE_DISABLE_CONFIRM}
        extra={fresh}
        onConfirm={() => toggle.mutate({ enabled: false, force: true })}
      />
    </div>
  );
}

/** Переключатель "Писать первым" с оптимистичным обновлением и откатом. */
export function OutreachToggle({
  account,
}: {
  account: TelegramAccount;
}): JSX.Element {
  const queryClient = useQueryClient();
  const key = keyOf(account);
  // KAN-871: с личного аккаунта менеджера рассылки нет.
  const personal = isPersonalTelegramAccount(account);

  const setOutreach = (
    list: TelegramAccount[] | undefined,
    value: boolean
  ): TelegramAccount[] | undefined =>
    list?.map((a) =>
      a.operator_id === account.operator_id && a.account_id === account.account_id
        ? { ...a, outreach: value }
        : a
    );

  const m = useMutation({
    mutationFn: (outreach: boolean) =>
      telegramAccountsApi.updateAccount(
        account.account_id,
        { outreach },
        account.operator_id
      ),
    onMutate: async (outreach) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEY });
      const prev = queryClient.getQueryData<TelegramAccount[]>(QUERY_KEY);
      queryClient.setQueryData<TelegramAccount[] | undefined>(
        QUERY_KEY,
        (list) => setOutreach(list, outreach)
      );
      return { prev };
    },
    onError: (err, _v, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(QUERY_KEY, ctx.prev);
      toast.error(outreachErrorMessage(err));
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });

  return (
    <div className="mt-1.5 flex items-center gap-2">
      <Switch
        className="h-5 w-9"
        checked={account.outreach && !personal}
        disabled={m.isPending || personal}
        aria-label="Писать первым"
        data-testid={`outreach-switch-${key}`}
        onCheckedChange={(v) => {
          if (!personal) m.mutate(v);
        }}
      />
      <span className="text-xs text-gray-500">Писать первым</span>
    </div>
  );
}
